import React, { useEffect, useState } from 'react';
import {
  Input,
  Button,
  Typography,
  Spin,
  Popover,
  Checkbox,
  Radio,
  Space,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import './styles/SmsConversationList.less';
import { Translation, getI18n } from 'react-i18next';
import useDebounce from '../../../elements/components/useDebounce';

import SmsConversationItem from './SmsConversationItem';
import BulkSmsMessageModalContainer from '../../smsMessages/containers/BulkSmsMessageModalContainer';

const { Text, Title } = Typography;

const SmsConversationList = ({
  onSelectSmsConversation,
  selectedSmsConversation,
  partnershipId,
  load,
  clearLists,
  limit,
  list,
  isLoading,
  total,
  sortOrder: propSortOrder,
  onSortOrderChange,
  smsConversationName,
  profileId,
}) => {
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 1000);
  const sortOrder = propSortOrder || 'desc';

  // Status constants matching backend
  const CONVERSATION_STATUS_OPEN = 1;
  const CONVERSATION_STATUS_RESOLVED = 2;
  const CONVERSATION_STATUS_READ = 3;
  const CONVERSATION_STATUS_UNREAD = 4;


  const [filters, setFilters] = useState({
    status: [CONVERSATION_STATUS_OPEN, CONVERSATION_STATUS_RESOLVED, CONVERSATION_STATUS_READ, CONVERSATION_STATUS_UNREAD],
  });
  const [filterKey, setFilterKey] = useState(0);

  // Separate conversations by assignment and filter by active status filters
  const listAssigned = list.filter(
    (conv) =>
      conv.assigned_to_profile?.id === profileId &&
      filters.status.includes(conv.conversation_status)
  );
  const listAll = list.filter(
    (conv) =>
      conv.assigned_to_profile?.id !== profileId &&
      filters.status.includes(conv.conversation_status)
  );

  // Update selected conversation with fresh data when list changes or filters change
  useEffect(() => {
    if (selectedSmsConversation) {
      const freshConversation = list.find(
        (conv) => conv.id === selectedSmsConversation.id
      );
      if (freshConversation) {
        // Check if conversation should be filtered out by status
        const shouldBeVisible = filters.status.includes(freshConversation.conversation_status);

        if (!shouldBeVisible) {
          // Conversation no longer matches active filters, deselect it
          onSelectSmsConversation(null);
        } else if (
          freshConversation.conversation_status !== selectedSmsConversation.conversation_status ||
          freshConversation.assigned_to_profile?.id !==
          selectedSmsConversation.assigned_to_profile?.id
        ) {
          // Update with fresh data if it changed
          onSelectSmsConversation(freshConversation);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, filters.status]);

  useEffect(() => {
    if (filters.status.length === 0) {
      clearLists();
      setCurrentPage(1);
      return;
    }

    clearLists();
    setCurrentPage(1);

    const timeoutId = setTimeout(() => {
      const filterObject = {
        status: filters.status,
        ...(debouncedSearchText.trim() !== '' && {
          search: debouncedSearchText,
        }),
      };
      load(partnershipId, 1, limit, sortOrder, filterObject);
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, limit, sortOrder, filterKey, debouncedSearchText]);

  // Deselect conversation when searching
  useEffect(() => {
    if (searchText.trim() !== '' && selectedSmsConversation) {
      onSelectSmsConversation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    if (scrollBottom < 50) {
      if (!isLoading && list.length < total) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        const filterObject = {
          status: filters.status,
          ...(debouncedSearchText.trim() !== '' && {
            search: debouncedSearchText,
          }),
        };
        load(partnershipId, nextPage, limit, sortOrder, filterObject);
      }
    }
  };

  const handleStatusChange = (status) => {
    setFilters((prev) => {
      const currentStatus = prev.status;
      const isSelected = currentStatus.includes(status);

      let newStatus;
      if (isSelected) {
        newStatus = currentStatus.filter((s) => s !== status);
      } else {
        newStatus = [...currentStatus, status];
      }

      // Sort by numeric value (1 = open, 2 = resolved)
      newStatus.sort((a, b) => a - b);

      return { ...prev, status: newStatus };
    });

    setSearchText('');
    setFilterKey((prev) => prev + 1);
  };

  const handleSortByChange = (sortBy) => {
    const newOrder = sortBy === 'most_recent' ? 'desc' : 'asc';
    if (onSortOrderChange) {
      onSortOrderChange(newOrder);
    }
  };

  const filterContent = (
    <div style={{ width: 280, padding: '10px 12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <FilterOutlined style={{ fontSize: 14, color: '#4279BD' }} />
        <Text strong style={{ fontSize: 14, color: '#4279BD' }}>
          {getI18n().t('sms_conversations_list_filters')}
        </Text>
      </div>

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1, paddingRight: 8 }}>
          <Text
            strong
            style={{
              fontSize: 14,
              color: '#4279BD',
              display: 'block',
              marginBottom: 10,
            }}
          >
            {getI18n().t('sms_conversations_list_status')}
          </Text>
          <Space direction='vertical' size={8}>
            <Checkbox
              checked={filters.status.includes(CONVERSATION_STATUS_OPEN)}
              onChange={() => handleStatusChange(CONVERSATION_STATUS_OPEN)}
            >
              {getI18n().t('sms_conversations_list_open')}
            </Checkbox>
            <Checkbox
              checked={filters.status.includes(CONVERSATION_STATUS_RESOLVED)}
              onChange={() => handleStatusChange(CONVERSATION_STATUS_RESOLVED)}
            >
              {getI18n().t('sms_conversations_list_resolved')}
            </Checkbox>
            <Checkbox
              checked={filters.status.includes(CONVERSATION_STATUS_READ)}
              onChange={() => handleStatusChange(CONVERSATION_STATUS_READ)}
            >
              {getI18n().t('sms_conversations_list_read')}
            </Checkbox>
            <Checkbox
              checked={filters.status.includes(CONVERSATION_STATUS_UNREAD)}
              onChange={() => handleStatusChange(CONVERSATION_STATUS_UNREAD)}
            >
              {getI18n().t('sms_conversations_list_unread')}
            </Checkbox>
          </Space>
        </div>

        <div
          style={{ flex: 1, paddingLeft: 8, borderLeft: '1px solid #f0f0f0' }}
        >
          <Text
            strong
            style={{
              fontSize: 14,
              color: '#4279BD',
              display: 'block',
              marginBottom: 10,
            }}
          >
            {getI18n().t('sms_conversations_list_sort_by')}
          </Text>
          <Radio.Group
            value={sortOrder === 'desc' ? 'most_recent' : 'longest_wait'}
            onChange={(e) => handleSortByChange(e.target.value)}
          >
            <Space direction='vertical' size={8}>
              <Radio value='most_recent'>
                {getI18n().t('sms_conversations_list_most_recent')}
              </Radio>
              <Radio value='longest_wait'>
                {getI18n().t('sms_conversations_list_longest_wait')}
              </Radio>
            </Space>
          </Radio.Group>
        </div>
      </div>
    </div>
  );

  return (
    <Translation>
      {(t) => (
        <>
          <div className='sms-conversation-list'>
            <div className='sms-conversation-list-header'>
              <div className='sms-conversation-list-header-component'>
                <Title level={4} className='sms-conversation-list-title'>
                  {getI18n().t('sms_conversations_list_chats')}
                </Title>

                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={() => setVisible(true)}
                >
                  {getI18n().t('sms_conversations_list_new_message')}
                </Button>
              </div>

              <div className='sms-conversation-search'>
                <Input
                  placeholder='Search...'
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={
                    <>
                      <SearchOutlined style={{ color: '#4279BD' }} />
                      {isLoading && searchText.trim() !== '' && (
                        <Spin size='small' style={{ marginLeft: 8 }} />
                      )}
                    </>
                  }
                  suffix={
                    <Popover
                      content={filterContent}
                      trigger='click'
                      open={filterVisible}
                      onOpenChange={setFilterVisible}
                      placement='bottom'
                      align={{ offset: [120, 0] }}
                      overlayClassName='sms-conversation-filter-popover'
                      overlayInnerStyle={{
                        borderRadius: '8px',
                        padding: '0px',
                        boxShadow:
                          '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <FilterOutlined
                        style={{ cursor: 'pointer', color: '#4279BD' }}
                      />
                    </Popover>
                  }
                />
              </div>

              {/* Filter Tags */}
              <div style={{ padding: '8px 0', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {/* Status Tags - can be closed */}
                {filters.status.includes(CONVERSATION_STATUS_OPEN) && (
                  <Tag
                    closable
                    onClose={() => handleStatusChange(CONVERSATION_STATUS_OPEN)}
                  >
                    {getI18n().t('sms_conversations_list_open')}
                  </Tag>
                )}
                {filters.status.includes(CONVERSATION_STATUS_RESOLVED) && (
                  <Tag
                    closable
                    onClose={() => handleStatusChange(CONVERSATION_STATUS_RESOLVED)}
                  >
                    {getI18n().t('sms_conversations_list_resolved')}
                  </Tag>
                )}
                {filters.status.includes(CONVERSATION_STATUS_READ) && (
                  <Tag
                    closable
                    onClose={() => handleStatusChange(CONVERSATION_STATUS_READ)}
                  >
                    {getI18n().t('sms_conversations_list_read')}
                  </Tag>
                )}
                {filters.status.includes(CONVERSATION_STATUS_UNREAD) && (
                  <Tag
                    closable
                    onClose={() => handleStatusChange(CONVERSATION_STATUS_UNREAD)}
                  >
                    {getI18n().t('sms_conversations_list_unread')}
                  </Tag>
                )}

                {/* Sort By Tag - cannot be closed, just shows current selection */}
                <Tag>
                  {sortOrder === 'desc'
                    ? getI18n().t('sms_conversations_list_most_recent')
                    : getI18n().t('sms_conversations_list_longest_wait')}
                </Tag>
              </div>
            </div>

            <div
              className='sms-conversation-list-content'
              onScroll={handleScroll}
            >
              {listAssigned.length > 0 && (
                <>
                  <Text strong className='sms-conversation-list-subtitle'>
                    {getI18n().t('sms_conversations_list_assign_to_me')}
                  </Text>

                  <div className='sms-conversation-section'>
                    {listAssigned.map((x) => (
                      <SmsConversationItem
                        key={x.key}
                        smsConversation={x}
                        onSelectSmsConversation={onSelectSmsConversation}
                        selectedSmsConversation={selectedSmsConversation}
                        smsConversationName={smsConversationName}
                      />
                    ))}
                  </div>
                </>
              )}

              {listAll.length > 0 && (
                <>
                  <Text strong className='sms-conversation-list-subtitle'>
                    {getI18n().t('sms_conversations_list_all')}
                  </Text>

                  <div className='sms-conversation-section'>
                    {listAll.map((x) => (
                      <SmsConversationItem
                        key={x.key}
                        smsConversation={x}
                        onSelectSmsConversation={onSelectSmsConversation}
                        selectedSmsConversation={selectedSmsConversation}
                        smsConversationName={smsConversationName}
                      />
                    ))}
                  </div>
                </>
              )}

              {isLoading && (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <Spin />
                </div>
              )}
            </div>
          </div>
          <BulkSmsMessageModalContainer
            visible={visible}
            setVisible={setVisible}
          />
        </>
      )}
    </Translation>
  );
};

export default SmsConversationList;
